import { Injectable, inject, DestroyRef, signal } from '@angular/core';
import { fromEvent, merge, timer, Subject, interval } from 'rxjs';
import { debounceTime, switchMap, tap, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface IdleConfig {
    idleTime: number;      // Time in seconds before considered idle
    timeoutTime: number;   // Time in seconds after idle before timeout
    keepaliveInterval?: number; // Keepalive ping interval in seconds
}

export interface IdleState {
    isIdle: boolean;
    isTimedOut: boolean;
    countdown: number | null;
    lastActivity: Date;
}

@Injectable({
    providedIn: 'root'
})
export class IdleDetectionService {
    private destroyRef = inject(DestroyRef);
    
    // Signals for reactive state
    private _state = signal<IdleState>({
        isIdle: false,
        isTimedOut: false,
        countdown: null,
        lastActivity: new Date()
    });
    
    // Observables for events
    private onIdleStart$ = new Subject<void>();
    private onIdleEnd$ = new Subject<void>();
    private onTimeout$ = new Subject<void>();
    private onTimeoutWarning$ = new Subject<number>();
    private onKeepalive$ = new Subject<Date>();
    private stop$ = new Subject<void>();
    
    private config: IdleConfig = {
        idleTime: 3600,
        timeoutTime: 120,
        keepaliveInterval: 15
    };
    
    private activityEvents = [
        'mousedown',
        'mousemove', 
        'keydown',
        'scroll',
        'touchstart',
        'click',
        'wheel'
    ];
    
    private isWatching = false;
    private countdownTimer$ = new Subject<void>();

    // Public observables
    get onIdleStart() { return this.onIdleStart$.asObservable(); }
    get onIdleEnd() { return this.onIdleEnd$.asObservable(); }
    get onTimeout() { return this.onTimeout$.asObservable(); }
    get onTimeoutWarning() { return this.onTimeoutWarning$.asObservable(); }
    get onKeepalive() { return this.onKeepalive$.asObservable(); }
    get state() { return this._state.asReadonly(); }

    setIdle(seconds: number): void {
        this.config.idleTime = seconds;
    }

    setTimeout(seconds: number): void {
        this.config.timeoutTime = seconds;
    }

    setKeepaliveInterval(seconds: number): void {
        this.config.keepaliveInterval = seconds;
    }

    watch(): void {
        if (this.isWatching) {
            return;
        }

        this.isWatching = true;
        this.stop$.next();
        
        // Reset state
        this._state.update(state => ({
            ...state,
            isIdle: false,
            isTimedOut: false,
            countdown: null,
            lastActivity: new Date()
        }));

        // Create activity stream from all user interaction events
        const activity$ = merge(
            ...this.activityEvents.map(event => 
                fromEvent(document, event)
            )
        ).pipe(
            debounceTime(500),
            takeUntil(this.stop$),
            takeUntilDestroyed(this.destroyRef)
        );

        // Setup idle detection
        activity$.pipe(
            tap(() => {
                // Update last activity
                this._state.update(state => ({
                    ...state,
                    lastActivity: new Date()
                }));
                
                // If was idle, emit idle end
                if (this._state().isIdle) {
                    this._state.update(state => ({
                        ...state,
                        isIdle: false,
                        countdown: null
                    }));
                    this.onIdleEnd$.next();
                    this.countdownTimer$.next();
                }
            }),
            switchMap(() => 
                // Wait for idle time with no activity
                timer(this.config.idleTime * 1000)
            )
        ).subscribe(() => {
            this.handleIdleStart();
        });

        // Setup keepalive ping if configured
        if (this.config.keepaliveInterval) {
            interval(this.config.keepaliveInterval * 1000).pipe(
                takeUntil(this.stop$),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
                if (!this._state().isIdle) {
                    this.onKeepalive$.next(new Date());
                }
            });
        }

        // Trigger initial activity to start the timer
        setTimeout(() => {
            document.dispatchEvent(new Event('mousedown'));
        }, 100);
    }

    private handleIdleStart(): void {
        this._state.update(state => ({
            ...state,
            isIdle: true,
            countdown: this.config.timeoutTime
        }));
        
        this.onIdleStart$.next();
        this.startTimeoutCountdown();
    }

    private countdownStartTimestamp: number | null = null;
    private startTimeoutCountdown(): void {
        this.countdownStartTimestamp = Date.now();
        const timeoutMs = this.config.timeoutTime * 1000;
        interval(1000).pipe(
            takeUntil(this.countdownTimer$),
            takeUntil(this.stop$),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            if (this.countdownStartTimestamp === null) return;
            const elapsed = Date.now() - this.countdownStartTimestamp;
            const remainingMs = timeoutMs - elapsed;
            const countdown = Math.ceil(remainingMs / 1000);
            this._state.update(state => ({
                ...state,
                countdown: countdown > 0 ? countdown : 0
            }));
            this.onTimeoutWarning$.next(countdown);
            if (remainingMs <= 0) {
                this.handleTimeout();
            }
        });
    }

    private handleTimeout(): void {
        this._state.update(state => ({
            ...state,
            isTimedOut: true,
            isIdle: false,
            countdown: null
        }));
        
        this.countdownTimer$.next();
        this.onTimeout$.next();
        this.stop();
    }

    stop(): void {
        this.isWatching = false;
        this.stop$.next();
        this.countdownTimer$.next();
        
        this._state.update(state => ({
            ...state,
            isIdle: false,
            isTimedOut: false,
            countdown: null
        }));
    }

    reset(): void {
        this.stop();
        this.watch();
    }
}
